import React, { useEffect } from 'react';
import { useOrganization } from './organization-provider';

const DEFAULT_PRIMARY_COLOR = '#002D2D';
const DEFAULT_SECONDARY_COLOR = '#26BEC2';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { settings } = useOrganization();
  
  useEffect(() => {
    const root = document.documentElement;
    
    // Apply primary color
    const primaryColor = settings?.primary_color || DEFAULT_PRIMARY_COLOR;
    root.style.setProperty('--org-primary', primaryColor);
    
    // Apply org accent (historically "secondary" in product settings)
    const secondaryColor = settings?.secondary_color || DEFAULT_SECONDARY_COLOR;
    root.style.setProperty('--org-secondary', secondaryColor);
    root.style.setProperty('--brand-accent', secondaryColor);
    root.style.setProperty('--brand-accent-foreground', contrastingForeground(secondaryColor));

    // Keep static brand turquoise vars aligned for legacy `brand.turquoise.*` usages
    root.style.setProperty('--brand-turquoise-secondary', secondaryColor);

    // Primary maps to org dark; UI "secondary" stays neutral in globals.css (shadcn-style)
    const primaryHSL = hexToHSL(primaryColor);
    root.style.setProperty('--primary', primaryHSL);
  }, [settings]);
  
  return <>{children}</>;
}

/**
 * Convert hex color to HSL format for Tailwind CSS
 * @param hex - Hex color string (e.g., "#002D2D")
 * @returns HSL string (e.g., "180 100% 9%")
 */
function hexToHSL(hex: string): string {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  
  // Convert to degrees and percentages
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  const lPercent = Math.round(l * 100);
  
  return `${h} ${s}% ${lPercent}%`;
}

/**
 * Pick white or near-black text for sufficient contrast on a solid hex background.
 */
function contrastingForeground(hex: string): string {
  const normalized = hex.replace('#', '').trim();
  if (normalized.length !== 6) {
    return '#ffffff';
  }
  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;
  const [R, G, B] = [r, g, b].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;
  return luminance > 0.45 ? DEFAULT_PRIMARY_COLOR : '#ffffff';
}

